import {
  isUnlocked,
  messageDeliveryFor,
  resolvePhoneShell,
  threadKeyFor,
} from 'magical-trails';
import { phonePaths } from '../components/Phone/phoneRoutes';
import {
  cachedTrailIsStale,
  fetchTrailPoint,
  getStoriesUsingCode,
  PointApiResponse,
  ReducedTrail,
} from 'magical-trails';

import {
  addPointToScannedPointQueue,
  apiError,
  loadSavedTrail,
  loadTrail,
  openStorySelector,
  restoreSavedTrail,
  setInviteRequired,
  setLoading,
  setPointScanned,
} from '../store/trail';
import store from '../store/index';
import { API_BASE_URL } from './apiBase';
import {
  fetchInviteOnlyPoint,
  mergeStoryProgress,
  syncTrailIfStale,
} from './liveTrailUpdates';
import { getSavedStory, getTrailStore } from './localStorage';
import { StorySelectorState } from '../store/trail/types';

// getCode navigates by pushing paths and does nothing else with the router
// history, so it depends on just that capability. The real react-router
// `useHistory()` result satisfies this, and a test can supply a minimal
// fixture without casting the whole History interface.
type NavHistory = { push: (path: string) => void };

// getStoriesUsingCode and cachedTrailIsStale now live in the shared
// `magical-trails` package (packages/magical-trails/src/pointResolution) so
// the web and native players share one implementation. fetchInviteOnlyPoint
// and mergeStoryProgress moved next to the live-update flow that owns them
// (./liveTrailUpdates). All four are re-exported here so existing consumers
// of './getCode' (and getCode.test.ts) are unaffected.
export {
  getStoriesUsingCode,
  cachedTrailIsStale,
  fetchInviteOnlyPoint,
  mergeStoryProgress,
};

export function navigateToStory(history: NavHistory, storyCode: string) {
  setTimeout(() => {
    history.push(`/trail/${storyCode}`);
  }, 100);
}

export function navigateToScannedPoint(
  history: NavHistory,
  storyCode: string,
  scannedCode: string
) {
  setTimeout(() => {
    history.push(`/trail/${storyCode}/trail-point/${scannedCode}`);
  }, 100);
}

export function loadStoryFromApiResponse(
  response: PointApiResponse,
  storyCode: string,
  scannedCode?: string
) {
  store.dispatch(loadTrail({ response, story: storyCode }));

  if (scannedCode) {
    store.dispatch(addPointToScannedPointQueue(scannedCode));
  }
}

export function loadStoryFromSavedTrail(
  trail: ReducedTrail,
  storyCode: string,
  scannedCode?: string
) {
  const storyProgress = getSavedStory(storyCode);

  if (storyProgress) {
    // Suppress loadSavedTrail's analytics: restoreSavedTrail fires the single,
    // correct load event under the persisted session token right after. Firing
    // both would log two load events with two different tokens.
    store.dispatch(
      loadSavedTrail({ trail, story: storyCode, suppressAnalytics: true })
    );
    store.dispatch(restoreSavedTrail(storyProgress));
  } else {
    store.dispatch(loadSavedTrail({ trail, story: storyCode }));
  }

  if (scannedCode) {
    store.dispatch(addPointToScannedPointQueue(scannedCode));
  }
}

export function selectStoryFromSelector(
  selector: StorySelectorState,
  storyCode: string,
  history: NavHistory
) {
  if (selector.source === 'api' && selector.response) {
    loadStoryFromApiResponse(
      selector.response,
      storyCode,
      selector.scannedCode
    );
  } else {
    loadStoryFromSavedTrail(selector.trail, storyCode, selector.scannedCode);
  }

  navigateToStory(history, storyCode);
}

function showStorySelector(selector: StorySelectorState) {
  store.dispatch(openStorySelector(selector));
}

function getStoredTrailForCode(code: string) {
  const { trail } = store.getState();
  const trailId = trail.codesInLocalStorage[code];
  return trailId ? getTrailStore()?.[trailId] : undefined;
}

function handleSavedTrailCode(
  code: string,
  storedTrail: ReducedTrail,
  history: NavHistory,
  source: StorySelectorState['source'] = 'saved'
) {
  // Serve the cache instantly (works offline), then check the live version in
  // the background so a republished trail swaps in without the player having
  // to manually reset.
  void syncTrailIfStale(code);

  const selectedStory = storedTrail.stories.find(
    (story) => story.code === code
  );

  if (selectedStory) {
    loadStoryFromSavedTrail(storedTrail, selectedStory.code);
    navigateToStory(history, selectedStory.code);
    return;
  }

  const storiesUsingLocationPoint = getStoriesUsingCode(storedTrail, code);

  if (storiesUsingLocationPoint.length === 1) {
    const storyCode = storiesUsingLocationPoint[0].code;
    loadStoryFromSavedTrail(storedTrail, storyCode, code);
    navigateToStory(history, storyCode);
  } else if (storiesUsingLocationPoint.length > 1) {
    showStorySelector({
      trail: storedTrail,
      stories: storiesUsingLocationPoint,
      scannedCode: code,
      source,
    });
  } else {
    store.dispatch(apiError(`Code ${code} was not found in saved trail`));
  }
}

const loadJsonFromCode = async (code: string, history: NavHistory) => {
  store.dispatch(setLoading());
  const fetched = await fetchTrailPoint(code, { baseUrl: API_BASE_URL });

  if (!fetched.ok && fetched.reason !== 'notFound') {
    store.dispatch(
      apiError(
        fetched.error ??
          `Something went wrong loading ${code} — please try again`
      )
    );
    return;
  }

  let getCodeResponse: PointApiResponse;
  if (fetched.ok) {
    getCodeResponse = fetched.data;
  } else {
    // Public path 404'd: try the authed invite-only path.
    const authed = await fetchInviteOnlyPoint(code);
    if (authed === 'inviteRequired') {
      store.dispatch(setInviteRequired({ code }));
      return;
    }
    if (authed === 'error') {
      store.dispatch(
        apiError(`Something went wrong loading ${code} — please try again`)
      );
      return;
    }
    if (authed === null) {
      store.dispatch(apiError(`Code ${code} was not found`));
      return;
    }
    getCodeResponse = authed;
  }

  if (getCodeResponse.type === 'story') {
    loadStoryFromApiResponse(getCodeResponse, code);
    // The /api/point response can come from the CDN's day-long
    // stale-while-revalidate window, so even a first scan can serve
    // yesterday's copy. The cheap version probe corrects that straight away.
    void syncTrailIfStale(code);
    navigateToStory(history, code);
    return;
  }

  if (getCodeResponse.type === 'location') {
    if (getCodeResponse.trail.stories.length === 1) {
      const storyCode = getCodeResponse.trail.stories[0].code;
      loadStoryFromApiResponse(getCodeResponse, storyCode, code);
      // See the story branch above: a freshly fetched payload can still be a
      // stale CDN copy.
      void syncTrailIfStale(code);
      navigateToStory(history, storyCode);
      return;
    }

    const storiesUsingLocationPoint = getStoriesUsingCode(
      getCodeResponse.trail,
      code
    );

    if (storiesUsingLocationPoint.length === 1) {
      const storyCode = storiesUsingLocationPoint[0].code;
      loadStoryFromApiResponse(getCodeResponse, storyCode, code);
      void syncTrailIfStale(code);
      navigateToStory(history, storyCode);
    } else if (storiesUsingLocationPoint.length > 1) {
      showStorySelector({
        response: getCodeResponse,
        trail: getCodeResponse.trail,
        stories: storiesUsingLocationPoint,
        scannedCode: code,
        source: 'api',
      });
    } else {
      store.dispatch(apiError(`Code ${code} was not found in API trail`));
    }
    return;
  }

  if (getCodeResponse.type === 'urlRedirect') {
    store.dispatch(apiError('URL redirects are not supported by this client'));
    return;
  }

  store.dispatch(apiError(`Unsupported code response type`));
};

const getCode = async (code: string, history: NavHistory) => {
  const { trail } = store.getState();

  if (trail.trailLoaded) {
    const currentStoryCode = trail.currentStory?.code;
    const codeIsInCurrentStory = trail.currentStory?.storyPoints
      .map((point) => point.code)
      .includes(code);

    if (currentStoryCode && codeIsInCurrentStory) {
      // Phone shell: message points carry codes too (other points refer to
      // them by code), but a message is not a poster. Typing or scanning its
      // code must not open it full screen, past its lock and its delay.
      const target = trail.currentStory?.storyPoints.find(
        (point) => point.code === code
      );
      const delivery =
        target && messageDeliveryFor(target, trail.currentStory?.config);
      if (target && delivery) {
        const landed =
          isUnlocked(target) &&
          target.deliveredAt !== undefined &&
          Date.parse(target.deliveredAt) <= Date.now();
        if (landed) {
          const threads = resolvePhoneShell(
            trail.currentStory?.config
          )?.threads;
          history.push(
            phonePaths.thread(
              `/trail/${currentStoryCode}`,
              threadKeyFor(delivery, threads ?? {})
            )
          );
        }
        return;
      }
      store.dispatch(setPointScanned(code));
      navigateToScannedPoint(history, currentStoryCode, code);
      return;
    }

    const storedTrail = getStoredTrailForCode(code);
    if (storedTrail) {
      handleSavedTrailCode(code, storedTrail, history, 'loaded');
      return;
    }

    await loadJsonFromCode(code, history);
    return;
  }

  const storedTrail = getStoredTrailForCode(code);
  if (storedTrail) {
    handleSavedTrailCode(code, storedTrail, history);
    return;
  }

  await loadJsonFromCode(code, history);
};

export default getCode;
